import React from "react";

// Components
import Avatar from "@material-ui/core/Avatar";
import Img from "gatsby-image";
import ListItem, { ListItemProps } from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";

// Types
import { ChildImageSharp } from "../../helpers/types";
import AuthorSocialNetworks from "./AuthorSocialNetworks";

// Styles
import { createStyles, withStyles, Theme } from "@material-ui/core/styles";

export type Author = {
  firstname: string;
  lastname: string;
  jobTitle: string;
  profilePic: {
    childImageSharp: ChildImageSharp;
  };

  // URL to the linkedin profile
  linkedin?: string;

  // URL to the github profile
  github?: string;

  // URL to the twitter profile
  twitter?: string;
};

export type AuthorCardProps<D extends React.ElementType = "div"> = {
  author: Author;

  /**
   * If true, the social networks will be shown
   *
   * @default false
   */
  showSocialNetworks?: boolean;
} & Omit<ListItemProps<D>, "button">;

function AuthorCard<D extends React.ElementType = "div">({
  author,
  showSocialNetworks,
  ...listItemProps
}: AuthorCardProps<D>): JSX.Element {
  const fullName = `${author.firstname} ${author.lastname}`;

  return (
    <ListItem component="div" disableGutters {...listItemProps}>
      <ListItemAvatar
        style={{
          minHeight: "auto",
          lineHeight: 0,
          minWidth: "auto",
          marginRight: 8,
        }}
      >
        <Avatar
          component={Img}
          fixed={author.profilePic.childImageSharp.fixed}
          alt={fullName}
        />
      </ListItemAvatar>
      <ListItemText
        primary={fullName}
        secondary={
          <>
            <span className="jobtitle">{author.jobTitle}</span>
            {showSocialNetworks ? (
              <>
                <br />
                <AuthorSocialNetworks author={author} />
              </>
            ) : null}
          </>
        }
      />
    </ListItem>
  );
}

const styles = (theme: Theme) =>
  createStyles({
    root: ({ showSocialNetworks }: AuthorCardProps) => {
      if (!showSocialNetworks) return {}; // Nothing to do

      // Align the items according to the ButtonIcon padding from the social
      // networks (12px)
      return {
        "& .MuiListItemText-primary": {
          paddingLeft: theme.spacing(1.5),
        },
        "& .jobtitle": {
          paddingLeft: theme.spacing(1.5),
        },
      };
    },
  });

export default withStyles(styles)(AuthorCard);
